import type { ComponentPropsWithRef } from "react";
import classNames from "classnames";
import { CloseHealth, closeHealthLabels } from "@common/types/closeHealth";
import styles from "./HealthIndicator.module.scss";

/** A health the chip can show; work with no health shows no chip. */
export type IndicatedHealth = Exclude<CloseHealth, CloseHealth.NONE>;

const TONE_CLASS_NAMES: Record<IndicatedHealth, string | undefined> = {
    [CloseHealth.AT_RISK]: styles.atRisk,
    [CloseHealth.WATCHING]: styles.watching,
    [CloseHealth.ON_TRACK]: styles.onTrack,
};

interface HealthIndicatorProps extends Omit<
    ComponentPropsWithRef<"span">,
    "children"
> {
    health: IndicatedHealth;
    /** `md` (default) for tables and panels; `sm` takes `Tag` sm's size, to line up in a tag row. */
    size?: "md" | "sm";
}

/**
 * Engagement health chip: a dot in the health's tone before its label
 * (On track, Watching, At risk). Extra span props pass through, so it can
 * be a `Tooltip` trigger on its own.
 */
export const HealthIndicator = ({
    health,
    size = "md",
    className,
    ...props
}: Readonly<HealthIndicatorProps>) => (
    <span
        {...props}
        className={classNames(
            styles.indicator,
            TONE_CLASS_NAMES[health],
            { [styles.small]: size === "sm" },
            className,
        )}
    >
        <span aria-hidden className={styles.dot} />
        {closeHealthLabels[health]}
    </span>
);
